import { PRIMITIVES } from '@calm/evexia-design-tokens';
import {
    MatchingChoicesPodData,
    MatchingPod,
} from '@rhg/hooks/use-learn-why/types';
import planMessages from '@rhg/lang/messages/plan';
import { List, Paper, Theme, Typography } from '@rhg/libs/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import SimpleListItem from '../simple-list-item';

const stylePod = {
    px: 6,
    pt: 6,
    pb: 2,
};

const stylePodTitle = {
    mb: 2,
};

export interface MatchingChoicePodProps extends MatchingChoicesPodData {
    type: MatchingPod;
}

const styleAboutYouItem = {
    my: 2,
    px: 0,
    '.MuiListItemText-primary': {
        fontSize: PRIMITIVES.typography.size._16,
        fontWeight: (theme: Theme) => theme.typography.body1.fontWeight!,
    },
    '.MuiListItemText-secondary': {
        fontWeight: PRIMITIVES.typography.weight._700,
    },
};

export function MatchingChoicePod({
    type,
    totalCount,
    matchingChoices,
}: MatchingChoicePodProps) {
    const { formatMessage } = useIntl();

    const matchesAll = useMemo(
        () => matchingChoices.length === totalCount,
        [matchingChoices.length, totalCount]
    );

    const title = useMemo(() => {
        switch (type) {
            case MatchingPod.Conditions:
                return matchesAll
                    ? formatMessage(
                          planMessages[
                              'plan.learn_why_modal.conditions_pod.title.all'
                          ]
                      )
                    : formatMessage(
                          planMessages[
                              'plan.learn_why_modal.conditions_pod.title.some'
                          ],
                          {
                              matching: matchingChoices.length,
                              total: totalCount,
                          }
                      );
            case MatchingPod.Topics:
                return matchesAll
                    ? formatMessage(
                          planMessages[
                              'plan.learn_why_modal.topics_pod.title.all'
                          ]
                      )
                    : formatMessage(
                          planMessages[
                              'plan.learn_why_modal.topics_pod.title.some'
                          ],
                          {
                              matching: matchingChoices.length,
                              total: totalCount,
                          }
                      );
            case MatchingPod.Goals:
                return matchesAll
                    ? formatMessage(
                          planMessages[
                              'plan.learn_why_modal.goals_pod.title.all'
                          ]
                      )
                    : formatMessage(
                          planMessages[
                              'plan.learn_why_modal.goals_pod.title.some'
                          ],
                          {
                              matching: matchingChoices.length,
                              total: totalCount,
                          }
                      );
            case MatchingPod.AgeGender:
                return formatMessage(
                    planMessages['plan.learn_why_modal.age_gender_pod.title']
                );
            case MatchingPod.Age:
                return formatMessage(
                    planMessages['plan.learn_why_modal.age_pod.title']
                );
            case MatchingPod.Gender:
                return formatMessage(
                    planMessages['plan.learn_why_modal.gender_pod.title']
                );
        }
    }, [matchesAll, matchingChoices.length, formatMessage, totalCount, type]);

    return (
        <Paper elevation={0} sx={stylePod}>
            <Typography variant="body1Demi" component="h2" sx={stylePodTitle}>
                {title}
            </Typography>
            <List>
                {matchingChoices.map(
                    ({ label, displayName, category, icon }) => (
                        <SimpleListItem
                            key={`topic-${label}`}
                            aria-label={displayName ?? ''}
                            disablePadding
                            primaryText={displayName ?? ''}
                            {...(category
                                ? { secondaryText: category?.name }
                                : {})}
                            leftIcon={icon}
                            sx={styleAboutYouItem}
                        />
                    )
                )}
            </List>
        </Paper>
    );
}
